import * as React from 'react';
import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { BrowserTitleService } from '../../../ts/services/BrowserTitleService';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { updateBreadcrumbs } from '../../actions/UpdateBreadcrumbsAction';
import { appStore } from '../../AppStore';
import { BreadcrumbStateInfo } from '../../content/breadcrumbs/BreadcrumbStateInfo';
import { ServiceLocator } from '../../ServiceLocator';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '../../state/SpaceInfo';
import { ConnectedQueryParamUpdateProvider } from '../RouterHelpers/QueryParamUpdateProvider';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { AllGroupsSpaceAngularProps, AllGroupsSpaceOwnProps } from './AllGroupsSpace';
import { ConnectedAllGroupsSpace } from './ConnectedAllGroupsSpace';

export interface AllGroupsSpaceProviderProps extends ReactDirectiveProps {
  readonly siteName: string;
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
}

const groupsSpaceInfo: SpaceInfo = { spaceType: 'groups', subspaceName: undefined, item: SpaceItemForNonEntitySpace };

export const AllGroupsSpaceProvider: React.FunctionComponent<AllGroupsSpaceProviderProps> = (props) => {

  React.useEffect(() => {
    appStore.dispatch(updateBreadcrumbs([getGroupsPlaceBreadcrumb(props.urls)]));
  }, []);

  return (
    <ConnectedSpaceInfoProvider spaceInfo={groupsSpaceInfo}>
      <ConnectedQueryParamUpdateProvider
        routeLocationPath={props.routeLocationPath}
        routeLocationSearch={props.routeLocationSearch}
        shouldUpdateBreadcrumbs={true}
        shouldUpdateContentTypes={false}
        shouldUpdateFilters={true}
        shouldUpdateSort={true}
      >
        <AngularWrappedAllGroupsSpace
          angularContext={props.angularContext}
          siteName={props.siteName}/>
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
};

const AllGroupsSpaceAngularHOC = angularProviderHOC<AllGroupsSpaceOwnProps, AllGroupsSpaceAngularProps, AllGroupsSpaceAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    browserTitleService: ServiceLocator.get<BrowserTitleService>('BrowserTitleService'),
    telemetry: ServiceLocator.get<Telemetry>('TelemetryService'),
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

const AngularWrappedAllGroupsSpace: React.ComponentType<AllGroupsSpaceOwnProps>
  = AllGroupsSpaceAngularHOC(ConnectedAllGroupsSpace);

export function getGroupsPlaceBreadcrumb(urls: SpacesSiteUrls): BreadcrumbStateInfo {
  const href = urls.groupsUrl();
  return {
    id: href,
    text: Messages.allGroups(),
    href: href,
    testId: 'groups-link'
  };
}
